import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Table, Form, Button, Dropdown, Spinner, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from 'react-toastify';
import { GetServiceCaller, PostServiceCaller } from "../../../../services/serviceCaller";
import { API_URL } from "../../../../constants";
import ExportToExcel from "../../../../components/ExportToExcel";
import Pagination from "../../../../components/Pagination";
import SearchBar from "../../../../components/SearchBar";
import Select from "react-select";

const AnnualAppraisalReport = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [showConfirm, setShowConfirm] = useState(false);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [sortDirection, setSortDirection] = useState("asc");

    const columns = useMemo(() => ({
        empId: { text: "EMP ID" },
        name: { text: "Name" },
        designation: { text: "Designation" },
        newDesignation: { text: "New Designation"},
        managerName: { text: "Manager Name" },
        doj: { text: "DOJ" },
        totalExp: { text: "Total. Exp" },
        NiceExp: { text: "Exp." },

        PrimarySkills: { text: "Primary Skills" },
        SecondarySkills: { text: "Secondary Skills" },

        gross: { text: "GROSS (A)" },
        prevGross: { text: "Prev. GROSS (B)" },
        grossIncPercent: { text: "Gross Inc.%"},
        pf: { text: "PF" },
        gratuity: { text: "Gratuity" },
        ctc: { text: "CTC" },
        basic: { text: "BASIC" },
        hra: { text: "HRA" },
        cAllow: { text: "C. ALLOW" },
        sAllow: { text: "S. ALLOW" },
        leaveEnc: { text: "Leave Enc." },
        cnInc: { text: "CN Inc." },
        ctInc: { text: "CT Inc." },
        trgInc: { text: "TRG Inc." },
        hrInc: { text: "HR Inc." },
        cca: { text: "CCA" },
        tmInc: { text: "TM Inc." },
        actMgmtInc: { text: "Act Mgmt Inc." },
        projInc: { text: "Proj. Inc." },
        inc: { text: "Inc." },
        vpf: { text: "VPF" },
        revInc: { text: "Rev. Inc." },
        jBonus: { text: "J. Bonus" },
        aprPercent: { text: "APR % (X)" },
        grading: { text: "Grading (Y)" },
        appPercentBand: { text: "APR % (Band)" },
        appAmtBand: { text: "APR Amt. (Band)" },
        appPercentMgmt: { text: "Appraisal % (as per Mgt)", style: { backgroundColor: "#ffc107" } },
        appAmtMgmt: { text: "Appr. Amt. (as per Mgt)" },
        appraisedGrossAZG: { text: "New Gross", style: { backgroundColor: "#f4c7f0" } },
        deltaGross: { text: "Gross Inc." },
        grossAmtIncPercent: { text: "Approx CTC (ARP)" },
        approxCtcAzg: { text: "Approx CTC (Mgt) NCTC" },
        approxPercentIncCtc: { text: "Approx. % Inc (CTC)" },
        AnnualRevenue: { text: "Annual Revenue" },
        GrossMargin: { text: "Gross Margin" },
        GrossProfit: { text: "Resource Cost" },
        Remarks: { text: "Remarks" }
    }), []);

    const [visibleColumns, setVisibleColumns] = useState(() => {
        const hiddenList = [
            'ctc', 'basic', 'hra', 'cAllow', 'sAllow', 'leaveEnc', 
            'cnInc', 'ctInc', 'trgInc', 'hrInc', 'cca', 'tmInc', 
            'actMgmtInc', 'projInc', 'inc', 'vpf', 'revInc', 'jBonus', 'pf', 'gratuity', 'GrossProfit', 'approxPercentIncCtc'
        ];
        return Object.keys(columns).reduce((acc, key) => ({
            ...acc,
            [key]: !hiddenList.includes(key)
        }), {});
    });

    const getExportData = () => {
        return currentTableData.map((item) => {
            const visibleItem = {};
            Object.keys(columns)
                .filter((columnName) => visibleColumns[columnName])
                .forEach((columnName) => {
                    visibleItem[columns[columnName].text] = item[columnName] !== undefined && item[columnName] !== null ? item[columnName] : '';
                });
            return visibleItem;
        });
    };

    const calculateRowMetrics = useCallback((row) => {
        const A = parseFloat(row.gross) || 0;
        const B = parseFloat(row.prevGross) || 0;
        const Z1 = parseFloat(row.appPercentBand) || 0;
        const Z3M = parseFloat(row.appPercentMgmt);

        const hasMgmtInput = !isNaN(Z3M) && row.appPercentMgmt !== "" && row.appPercentMgmt !== null;

        const appAmtBandValue = (Z1 * A) / 100;
        const appAmtMgmtValue = hasMgmtInput ? (Z3M * A) / 100 : 0;
        const appraisedGrossAZGValue = hasMgmtInput ? (A + appAmtMgmtValue) : (A + appAmtBandValue);
        const deltaGrossValue = appraisedGrossAZGValue - A;
        const ctc = row.ctc;
        
        return {
            ...row,
            grossIncPercent: B !== 0 ? (((A - B) / B) * 100).toFixed(2) + "%" : "0%",
            appAmtBand: Math.round(appAmtBandValue).toLocaleString(),
            appAmtMgmt: hasMgmtInput ? Math.round(appAmtMgmtValue).toLocaleString() : " ",
            appraisedGrossAZG: Math.round(appraisedGrossAZGValue).toLocaleString(),
            deltaGross: Math.round(deltaGrossValue).toLocaleString(),
            grossAmtIncPercent: A !== 0 ? Math.round((appAmtBandValue + A) * 14).toLocaleString() : "0",
            approxCtcAzg: Math.round(appraisedGrossAZGValue * 14).toLocaleString(),
            approxPercentIncCtc: A !== 0 ? ((((appraisedGrossAZGValue * 14) - ctc) / ctc) * 100).toFixed(2) + "%" : "0%",
            rawValues: {
                appAmtBand: appAmtBandValue,
                appAmtMgmt: appAmtMgmtValue,
                appraisedGrossAZG: appraisedGrossAZGValue,
                deltaGross: deltaGrossValue,
                grossAmtIncPercent: A !== 0 ? (deltaGrossValue / A) * 100 : 0,
                approxCtc: appraisedGrossAZGValue * 13
            }
        };
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await GetServiceCaller(`${API_URL}get-appraisal-working-data`);
            if (response.data.status === 'success' && response.data.data) {
                const dList = response.data.designation || [];
                setDesignationOptions(dList);
                const transformed = response.data.data.map((item, idx) => {
                    const existingMgmtPercent = item.ApprPercentMgmt || "";
                    const existingRemarks = item.Remarks || "";
                    let initialId = item.NewDesignation ? String(item.NewDesignation) : "";
                    if (!initialId || initialId === "0") {
                    const match = dList.find(
                        (opt) => opt.text?.trim().toLowerCase() === item.Designation?.trim().toLowerCase()
                    );
                    initialId = match ? String(match.value) : "";
                    }
                    
                    const roundValue = (value) => {
                        if (value === null || value === undefined || value === "") return value;
                        const num = parseFloat(value);
                        return isNaN(num) ? value : Math.round(num);
                    };

                    return calculateRowMetrics({
                        id: idx,
                        cycleId: item.CycleID || 21,
                        empId: item.EmpCode,
                        name: item.EmpName,
                        designation: item.Designation,
                        newDesignationId: initialId,
                        DesignationID: item.DesignationID,
                        managerName: item.Manager,
                        doj: item.DOJ,
                        totalExp: item.TotalExp,
                        NiceExp: item.NiceExp,
                        // Add skills from API response (same as Bonus Report)
                        PrimarySkills: item.PrimarySkills || "",
                        SecondarySkills: item.SecondarySkills || "",
                        
                        gross: roundValue(item.Gross),
                        prevGross: roundValue(item.PrevGross),
                        pf: roundValue(item.PF),
                        gratuity: roundValue(item.Gratuity),
                        ctc: roundValue(item.CurrentCTC),
                        basic: roundValue(item.Basic),
                        hra: roundValue(item.HRA),
                        cAllow: roundValue(item.CAllowance),
                        sAllow: roundValue(item.SAllowance),
                        leaveEnc: roundValue(item.LeaveEncash),
                        cnInc: roundValue(item.ConsultIncentive),
                        ctInc: roundValue(item.ContentIncentive),
                        trgInc: roundValue(item.TrainingIncentive),
                        hrInc: roundValue(item.HRIncentive),
                        cca: roundValue(item.CCA),
                        tmInc: roundValue(item.TeamIncentive),
                        actMgmtInc: roundValue(item.Acount_Management_INC),
                        projInc: roundValue(item.ProjectIncentive),
                        inc: roundValue(item.Incentives),
                        vpf: roundValue(item.VPF),
                        revInc: roundValue(item.Rev_Inc),
                        jBonus: roundValue(item.JoiningBonus),
                        aprPercent: item.APRPercentage,
                        grading: item.Grading,
                        appPercentBand: parseFloat(item.aprPercentAsPerBand) || 0,
                        Remarks: existingRemarks,
                        appPercentMgmt: existingMgmtPercent,
                        PayrollIncrementCheck: Number(item.PayrollIncrementCheck),
                        joinSeptCheck: Number(item.joinSeptCheck),
                        DubaiShiftCheck: Number(item.DubaiShiftCheck),
                        AnnualRevenue: roundValue(item.AnnualRevenue),
                        GrossMargin: item.GrossMargin ? `${item.GrossMargin}%` : "0%",
                        GrossProfit: roundValue(item.GrossProfit),
                    });
                });
                setData(transformed);
            }
        } catch (error) {
            toast.error("Error loading data");
        } finally {
            setLoading(false);
        }
    };

    const formatDateToISO = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return !isNaN(date) ? date.toISOString().split('T')[0] : dateStr;
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const submitData = data.map(item => ({
                EmpID: item.empId,
                CycleID: item.cycleId || 21,
                EmpName: item.name,
                Designation: item.designation,
                ManagerName: item.managerName,
                DOJ: formatDateToISO(item.doj),
                TotalExp: item.totalExp,
                NiceExp: item.NiceExp,
                Gross_A: String(item.gross),
                Prev_Gross_B: String(item.prevGross),
                GrossIncPercent: item.grossIncPercent.replace('%', ''),
                APRPercent: item.aprPercent.replace('%', ''),
                Grading: item.grading,
                ApprPercentBand: item.appPercentBand,
                ApprAmtBand: item.rawValues.appAmtBand,
                ApprPercentMgmt: parseFloat(item.appPercentMgmt),
                ApprAmtMgmt: item.rawValues.appAmtMgmt,
                AppraisedGross_AZG: item.rawValues.appraisedGrossAZG,
                DeltaGross: item.rawValues.deltaGross,
                GrossAmtIncPercent: parseFloat(item.rawValues.grossAmtIncPercent.toFixed(2)),
                ApproxCTC: String(item.rawValues.approxCtc.toFixed(0)),
                ApproxCTCPercent: item.approxPercentIncCtc.replace('%', ''),
                NewDesignationID: item.newDesignationId ? parseInt(item.newDesignationId) : null,
                DesignationID: item.DesignationID ? parseInt(item.DesignationID) : null,
                Remarks: item.Remarks
            }));
            const apiEndpoint = `${API_URL}store-appraisal-working-data`;
            const response = await PostServiceCaller(apiEndpoint, submitData, "post");

            if (response.data.message === 'successfully created') {
                toast.success("Data submitted successfully!");
            } else {
                toast.error(response.data.message || "Submission failed");
            }
        } catch (error) {
            toast.error("Error connecting to server");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const filteredData = useMemo(() => {
    const filtered = data.filter(item =>
        item.empId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.managerName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return [...filtered].sort((a, b) => {
        const valA = String(a.empId);
        const valB = String(b.empId);

        if (sortDirection === "asc") {
            return valA.localeCompare(valB, undefined, { numeric: true });
        } else {
            return valB.localeCompare(valA, undefined, { numeric: true });
        }
    });
}, [data, searchTerm, sortDirection]);

    const currentTableData = useMemo(() => {
        if (recordsPerPage === "ALL") return filteredData;
        const start = (currentPage - 1) * parseInt(recordsPerPage);
        return filteredData.slice(start, start + parseInt(recordsPerPage));
    }, [currentPage, filteredData, recordsPerPage]);

    const summaryTotals = useMemo(() => {
        if (!data.length) return { totalGrossA: 0, totalNewGross: 0, totalGrossInc: 0 };
        return data.reduce((acc, item) => {
            const grossA = parseFloat(String(item.gross).replace(/,/g, '')) || 0;
            const newGross = parseFloat(String(item.appraisedGrossAZG).replace(/,/g, '')) || 0;
            const grossInc = parseFloat(String(item.deltaGross).replace(/,/g, '')) || 0;
            return {
                totalGrossA: acc.totalGrossA + grossA,
                totalNewGross: acc.totalNewGross + newGross,
                totalGrossInc: acc.totalGrossInc + grossInc
            };
        }, { totalGrossA: 0, totalNewGross: 0, totalGrossInc: 0 });
    }, [data]);

    const formattedSummary = useMemo(() => ({
            totalGrossA: summaryTotals.totalGrossA.toLocaleString(),
            totalNewGross: summaryTotals.totalNewGross.toLocaleString(),
            totalGrossInc: summaryTotals.totalGrossInc.toLocaleString()
    }), [summaryTotals]);

    return (
        <div className="bg-white p-3 shadow-sm rounded">
            <style>
                {`
                .custom-scrollbar::-webkit-scrollbar {
                    height: 12px;
                    width: 12px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f8f9fa;
                    border-radius: 10px;
                    border: 1px solid #dee2e6;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: #27568b;
                    border-radius: 10px;
                    border: 3px solid #f8f9fa;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background-color: #1d4570;
                }
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: #27568b #f8f9fa;
                }
                `}
            </style>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div style={{ width: "240px" }}>
                    <SearchBar searchTerm={searchTerm} setSearchTerm={(v) => { setSearchTerm(v); setCurrentPage(1); }} />
                </div>
                <div className="d-flex gap-2">
                    <ExportToExcel data={getExportData()} filename="AnnualAppraisalReport.csv" />
                    <Dropdown autoClose="outside">
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className="bi bi-grid-3x3-gap me-1"></i> Columns
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="p-2 shadow border-0" style={{ maxHeight: "400px", overflowY: "auto", minWidth: "220px" }}>
                            <div className="d-flex justify-content-between border-bottom mb-2 pb-1">
                                <Button variant="link" size="sm" className="p-0 text-decoration-none fw-bold" onClick={() => setVisibleColumns(Object.keys(columns).reduce((acc, k) => ({ ...acc, [k]: true }), {}))}>All</Button>
                                <Button variant="link" size="sm" className="p-0 text-decoration-none text-danger fw-bold" onClick={() => setVisibleColumns(Object.keys(columns).reduce((acc, k) => ({ ...acc, [k]: false }), {}))}>None</Button>
                            </div>
                            {Object.keys(columns).map(key => (
                                <Form.Check
                                    key={key}
                                    type="checkbox"
                                    label={columns[key].text}
                                    className="small mb-1"
                                    checked={visibleColumns[key]}
                                    onChange={() => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }))}
                                />
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>

                    <Button
                        style={{ backgroundColor: "rgb(39, 86, 139)" }}
                        size="sm"
                        className="px-3"
                        onClick={() => setShowConfirm(true)}
                        disabled={submitting || loading}
                    >
                        {submitting ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-send-fill me-1"></i>}
                        Submit
                    </Button>
                </div>
            </div>

            <div className="mb-3 p-2 rounded d-flex flex-wrap align-items-center gap-4" style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", fontSize: "11px" }}>
    <div className="d-flex align-items-center gap-2">
                    <div style={{ width: "16px", height: "16px", backgroundColor: "#D4EDDA", border: "1px solid #e0d1a0", borderRadius: "3px" }}></div>
        <span className="fw-bold">Payroll Increment After April</span>
    </div>
    <div className="d-flex align-items-center gap-2">
                    <div style={{ width: "16px", height: "16px", backgroundColor: "#CCE5FF", border: "1px solid #b38f2d", borderRadius: "3px" }}></div>
        <span className="fw-bold">Dubai Shift</span>
    </div>
    <div className="d-flex align-items-center gap-2">
                    <div style={{ width: "16px", height: "16px", backgroundColor: "#FFE5D0", border: "1px solid #00bb16", borderRadius: "3px" }}></div>
        <span className="fw-bold">Joiners After Oct.</span>
    </div>
        <div style={{ width: "1px", height: "20px", backgroundColor: "#dee2e6" }}></div>
            {loading ? (
                <div className="d-flex align-items-center gap-2">
                    <Spinner size="sm" variant="primary" />
                    <span className="text-muted">Loading totals...</span>
                </div>
            ) : data.length > 0 ? (
                // Actual summary values
                <>
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold">GROSS (A):</span>
                        <span className="bg-white px-2 py-1 rounded border" style={{ minWidth: "80px" }}>
                            ₹{formattedSummary?.totalGrossA || "0"}
                        </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold">New Gross:</span>
                        <span className="bg-white px-2 py-1 rounded border" style={{ minWidth: "80px" }}>
                            ₹{formattedSummary?.totalNewGross || "0"}
                        </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold">Gross Inc.:</span>
                        <span className="bg-white px-2 py-1 rounded border" style={{ minWidth: "80px" }}>
                            ₹{formattedSummary?.totalGrossInc || "0"}
                        </span>
                    </div>
                    <div className="text-muted small border-start ps-2" style={{ borderColor: "#dee2e6" }}>
                        <i className="bi bi-people-fill me-1"></i>
                        {data.length} employees
                    </div>
                </>
            ) : (
                // No data state
                <span className="text-muted">No data available</span>
            )}
        </div>

            {/* STICKY TABLE CONTAINER */}
            <div className="border rounded position-relative custom-scrollbar" 
                 style={{ 
                    overflow: "auto", 
                    maxHeight: "650px", 
                    minHeight: "350px", 
                    width: "100%",
                    backgroundColor: "#fff",
                    display: "block"
                 }}>
                
                {(loading || submitting) && (
                    <div className="position-absolute w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white" style={{ zIndex: 100, opacity: 0.8 }}>
                        <Spinner animation="border" variant="primary" />
                        <span className="mt-2 fw-bold text-primary">{submitting ? "Saving Changes..." : "Fetching Appraisal Data..."}</span>
                    </div>
                )}

                <Table hover bordered className="mb-0 align-middle text-nowrap" 
                       style={{ fontSize: "11px", borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            {Object.keys(columns).map(key => {
                                if (!visibleColumns[key]) return null;
                                
                                // Sticky Column Logic
                                const isEmpId = key === 'empId';
                                const isName = key === 'name';
                                const stickyLeft = isEmpId ? 0 : (isName ? "70px" : "auto");

                                return (
                                    <th 
                                        key={key} 
                                        className="text-white text-center py-2 border-light" 
                                        onClick={() => {
                                            if (isEmpId) {
                                                setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
                                            }
                                        }}
                                        style={{ 
                                            position: "sticky",
                                            top: 0,
                                            left: stickyLeft,
                                            zIndex: (isEmpId || isName) ? 40 : 20, // Corners need highest z-index
                                            backgroundColor: columns[key].style?.backgroundColor || "#27568b",
                                            borderBottom: "2px solid #dee2e6",
                                            borderRight: "1px solid #dee2e6"
                                        }}
                                    >
                                        <span style={{ color: columns[key].style?.backgroundColor ? "#000" : "#fff" }}>
                                            {columns[key].text}
                                        </span>
                                        {isEmpId && (
                                            <i className={`bi bi-sort-numeric-${sortDirection === "asc" ? "down" : "up"}`}></i>
                                        )}
                                </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && currentTableData.map(row => {
                            const isPayrollCheckHighlighted = String(row.PayrollIncrementCheck) === "1";
                            const isjoinafterSeptHighlighted = String(row.joinSeptCheck) === "1";
                            const isDubaiShiftCheckHighlighted = String(row.DubaiShiftCheck) === "1";
                            
                            return (
                                <tr key={row.id}>
                                    {Object.keys(columns).map(key => {
                                        if (!visibleColumns[key]) return null;

                                        const isEmpId = key === 'empId';
                                        const isName = key === 'name';
                                        const stickyLeft = isEmpId ? 0 : (isName ? "70px" : "auto");

                                        // BASE STYLES FOR ALL CELLS
                                        let cellStyle = {
                                            position: (isEmpId || isName) ? "sticky" : "static",
                                            left: stickyLeft,
                                            zIndex: (isEmpId || isName) ? 10 : 1,
                                            backgroundColor: "#fff", // Default background to prevent transparency
                                            borderRight: "1px solid #dee2e6",
                                            borderBottom: "1px solid #dee2e6"
                                        };

                                        // APPLY CONDITIONAL HIGHLIGHTING
                                        if (key === 'appraisedGrossAZG') {
                                            cellStyle.backgroundColor = '#fdf2fd';
                                        } else if (isPayrollCheckHighlighted) {
                                            cellStyle.backgroundColor = '#D4EDDA';
                                        } else if (isDubaiShiftCheckHighlighted) {
                                            cellStyle.backgroundColor = '#CCE5FF';
                                        } else if (isjoinafterSeptHighlighted) {
                                            cellStyle.backgroundColor = '#FFE5D0';
                                        }
                                        if (key === 'PrimarySkills' || key === 'SecondarySkills') {
                                            return (
                                                <td
                                                    key={key}
                                                    className="text-start px-2"
                                                    style={{
                                                        ...cellStyle,
                                                        maxWidth: "180px",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}
                                                >
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id={`tooltip-${key}-${row.id}`}>
                                                                {row[key] || "No skills added"}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span style={{ cursor: "help" }}>
                                                            {row[key] || "-"}
                                                        </span>
                                                    </OverlayTrigger>
                                                </td>
                                            );
                                        }
                                        // SPECIAL INPUT RENDERING
                                        if (key === 'appPercentMgmt') return (
                                            <td key={key} className="p-1" style={{ ...cellStyle, backgroundColor: "#fffde7", width: "90px" }}>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    className="text-center fw-bold border-warning p-0 shadow-none"
                                                    style={{ height: "24px", fontSize: "11px" }}
                                                    value={row[key]}
                                                    onChange={(e) => setData(prev => prev.map(r => r.id === row.id ? calculateRowMetrics({ ...r, appPercentMgmt: e.target.value }) : r))}
                                                />
                                            </td>
                                        );

                                        if (key === 'newDesignation') {
                                            // Find the currently selected option
                                            const selectedOption = designationOptions.find(
                                                opt => String(opt.value) === String(row.newDesignationId)
                                            ) || null;

                                            return (
                                                <td key={key} className="p-1" style={{ ...cellStyle, minWidth: "200px", backgroundColor: "#f0f7ff" }}>
                                                    <Select
                                                        name="newDesignation"
                                                        options={designationOptions.map(opt => ({
                                                            value: String(opt.value),
                                                            label: opt.text
                                                        }))}
                                                        value={selectedOption ? { value: String(selectedOption.value), label: selectedOption.text } : null}
                                                        onChange={(selectedOption) => {
                                                            setData(prev => prev.map(r => 
                                                                r.id === row.id ? { ...r, newDesignationId: selectedOption?.value || "" } : r
                                                            ));
                                                        }}
                                                        placeholder="-- Select Designation --"
                                                        isClearable={false}  // Disable clear functionality
                                                        menuPortalTarget={document.body}
                                                        components={{
                                                            DropdownIndicator: null,  // Remove dropdown arrow
                                                            IndicatorSeparator: null,  // Remove separator line
                                                            ClearIndicator: null  // Remove clear indicator (cross)
                                                        }}
                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                minHeight: "26px",
                                                                height: "26px",
                                                                fontSize: "11px",
                                                                borderColor: "#dee2e6",
                                                                boxShadow: "none",
                                                                '&:hover': {
                                                                    borderColor: "#27568b"
                                                                },
                                                                cursor: "pointer",
                                                                backgroundColor: "#fff"
                                                            }),
                                                            valueContainer: (base) => ({
                                                                ...base,
                                                                height: "24px",
                                                                padding: "0 6px"
                                                            }),
                                                            input: (base) => ({
                                                                ...base,
                                                                margin: 0,
                                                                padding: 0
                                                            }),
                                                            // Removed indicatorsContainer since we removed indicators
                                                            menu: (base) => ({
                                                                ...base,
                                                                fontSize: "11px",
                                                                zIndex: 9999
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                fontSize: "11px",
                                                                padding: "4px 8px",
                                                                backgroundColor: state.isSelected ? "#27568b" : (state.isFocused ? "#e6f0ff" : "white"),
                                                                color: state.isSelected ? "white" : "#333",
                                                                cursor: "pointer"
                                                            }),
                                                            menuPortal: (base) => ({
                                                                ...base,
                                                                zIndex: 9999
                                                            }),
                                                            placeholder: (base) => ({
                                                                ...base,
                                                                fontSize: "11px",
                                                                color: "#6c757d"
                                                            }),
                                                            singleValue: (base) => ({
                                                                ...base,
                                                                fontSize: "11px"
                                                            })
                                                        }}
                                                    />
                                                </td>
                                            );
                                        }

                                        if (key === 'Remarks') return (
                                            <td key={key} className="p-1" style={{ ...cellStyle, minWidth: "150px" }}>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    className="p-1 shadow-none"
                                                    placeholder="Enter remark..."
                                                    style={{ height: "24px", fontSize: "11px" }}
                                                    value={row[key] || ""}
                                                    onChange={(e) => setData(prev => prev.map(r => r.id === row.id ? { ...r, Remarks: e.target.value } : r))}
                                                />
                                            </td>
                                        );

                                        return (
                                            <td
                                                key={key}
                                                className={`${(key === 'designation' || key === 'name' || key === 'managerName') ? 'text-start' : 'text-center'} px-2 ${key === 'appraisedGrossAZG' ? 'fw-bold' : ''}`}
                                                style={cellStyle}
                                            >
                                                {(row[key] !== undefined && row[key] !== null && row[key] !== "") ? row[key] : "-"}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            {/* Pagination and Modal (Same as original) */}
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered size="sm">
                <Modal.Body className="p-4 text-center">
                    <div className="mb-3"><i className="bi bi-question-circle text-primary" style={{ fontSize: "3rem" }}></i></div>
                    <h5 className="fw-bold mb-2">Ready to submit?</h5>
                    <p className="text-secondary small mb-4">Please review your entries. Once submitted, these scores will be finalized for this cycle.</p>
                    <div className="d-grid gap-2">
                        <Button style={{ backgroundColor: "#27568b", border: "none" }} onClick={() => { setShowConfirm(false); handleSubmit(); }}>Yes, Submit Now</Button>
                        <Button variant="light" className="text-secondary border" onClick={() => setShowConfirm(false)}>No, Go Back</Button>
                    </div>
                </Modal.Body>
            </Modal>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-3">
                    <small className="text-muted">Showing {currentTableData.length} of {filteredData.length} records</small>
                    <Form.Select size="sm" style={{ width: "85px", fontSize: "11px" }} value={recordsPerPage} onChange={(e) => { setRecordsPerPage(e.target.value); setCurrentPage(1); }}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="ALL">ALL</option>
                    </Form.Select>
                </div>
                {recordsPerPage !== "ALL" && !loading && (
                    <Pagination currentPage={currentPage} totalCount={filteredData.length} pageSize={parseInt(recordsPerPage)} onPageChange={p => setCurrentPage(p)} />
                )}
            </div>
        </div>
    );
};

export default AnnualAppraisalReport;